import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Calendar as CalIcon, Play, CheckCircle2, X as XIcon, Receipt, Eye, Loader2, Plus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import InvoiceCreateDialog from "@/components/admin/InvoiceCreateDialog";
import NewJobDialog from "@/components/admin/NewJobDialog";
import api, { apiError } from "@/lib/api";

const jobStatusColor = {
  unscheduled: "bg-amber-500/15 text-amber-600",
  scheduled: "bg-blue-500/15 text-blue-600",
  in_progress: "bg-purple-500/15 text-purple-600",
  completed: "bg-emerald-500/15 text-emerald-600",
  cancelled: "bg-zinc-500/15 text-zinc-600",
};
const PERIODS = [
  { v: "all", label: "All time" }, { v: "today", label: "Today" },
  { v: "yesterday", label: "Yesterday" }, { v: "this_week", label: "This week" },
  { v: "this_month", label: "This month" }, { v: "tomorrow", label: "Tomorrow" },
  { v: "next_week", label: "Next week" }, { v: "next_month", label: "Next month" },
];

export default function JobsTab() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState("all");
  const [period, setPeriod] = useState("all");
  const [scheduling, setScheduling] = useState(null); // job being scheduled
  const [scheduleDates, setScheduleDates] = useState([]); // ["2026-09-10", ...]
  const [dateInput, setDateInput] = useState("");
  const [dayInfo, setDayInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [invoiceJob, setInvoiceJob] = useState(null);
  const [newJobOpen, setNewJobOpen] = useState(false);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/jobs", { params: { q, status, period } });
    setItems(data);
  }, [q, status, period]);
  useEffect(() => { load(); }, [load]);

  const checkDate = async (date) => {
    if (!date) { setDayInfo(null); return; }
    try { const { data } = await api.get("/admin/bookings/slots", { params: { date } }); setDayInfo(data); }
    catch { setDayInfo(null); }
  };

  const addDate = () => {
    if (!dateInput) return;
    if (dayInfo?.closed) { toast.error("Closed Sundays — pick another day"); return; }
    if (scheduleDates.includes(dateInput)) { setDateInput(""); return; }
    setScheduleDates([...scheduleDates, dateInput].sort());
    setDateInput("");
    setDayInfo(null);
  };
  const removeDate = (d) => setScheduleDates(scheduleDates.filter(x => x !== d));

  const openSchedule = (job) => {
    setScheduling(job);
    setScheduleDates((job.slots || []).map(s => s.date).sort());
    setDateInput("");
    setDayInfo(null);
  };

  const saveSchedule = async () => {
    setBusy(true);
    try {
      if (scheduleDates.length === 0) { toast.error("Add at least one date"); return; }
      await api.post(`/admin/jobs/${scheduling.job_id}/schedule`, scheduleDates.map(date => ({ date })));
      toast.success("Job scheduled — customer notified"); setScheduling(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const setJobStatus = async (id, s, jobRow) => {
    try {
      await api.post(`/admin/jobs/${id}/status`, { status: s });
      toast.success(`Marked ${s}`);
      if (s === "completed" && jobRow && !jobRow.invoice_id) {
        setInvoiceJob({ ...jobRow, status: "completed" });
      }
      load();
    }
    catch (e) { toast.error(apiError(e)); }
  };

  const generateInvoice = (job) => setInvoiceJob(job);
  const viewInvoice = (invoiceId) => nav(`/invoice/${invoiceId}`);

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Jobs</h1>
            <p className="text-sm text-muted-foreground mt-1">Accepted quotes automatically become jobs. Schedule them into the calendar below.</p>
          </div>
          <Button onClick={() => setNewJobOpen(true)} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="new-job-btn">
            <Plus className="h-4 w-4 mr-2" />New Job
          </Button>
        </div>
      </div>
      <Card className="p-3">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search customer, address, phone…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" data-testid="jobs-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44" data-testid="jobs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{["all", "unscheduled", "scheduled", "in_progress", "completed", "cancelled"].map(s => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44" data-testid="jobs-period-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      {items.length === 0 ? (
        <Card className="p-16 text-center text-muted-foreground">No jobs match these filters.</Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="p-3">Customer</th><th className="p-3">Address</th><th className="p-3">Value</th>
                  <th className="p-3">Slots</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(j => (
                  <tr key={j.job_id} className="border-t border-border hover:bg-secondary/40" data-testid={`job-row-${j.job_id}`}>
                    <td className="p-3"><div className="font-medium">{j.customer_name}</div><div className="text-xs text-muted-foreground">{j.customer_phone}</div></td>
                    <td className="p-3 max-w-xs truncate">{j.address}</td>
                    <td className="p-3 font-semibold">${(j.price || 0).toLocaleString()}</td>
                    <td className="p-3 text-xs">{j.slots?.length ? j.slots.map(s => s.date).join(", ") : <span className="text-muted-foreground">—</span>}</td>
                    <td className="p-3"><Badge className={jobStatusColor[j.status] + " border-0"}>{j.status.replace("_", " ")}</Badge></td>
                    <td className="p-3">
                      <div className="flex justify-end gap-1">
                        {j.status !== "completed" && j.status !== "cancelled" && (
                          <Button size="sm" variant="ghost" onClick={() => openSchedule(j)} data-testid={`job-schedule-${j.job_id}`} title="Schedule"><CalIcon className="h-4 w-4 text-eagle-gold" /></Button>
                        )}
                        {j.status === "scheduled" && <Button size="sm" variant="ghost" onClick={() => setJobStatus(j.job_id, "in_progress")} title="Start"><Play className="h-4 w-4 text-purple-600" /></Button>}
                        {(j.status === "scheduled" || j.status === "in_progress") && <Button size="sm" variant="ghost" onClick={() => setJobStatus(j.job_id, "completed", j)} title="Complete"><CheckCircle2 className="h-4 w-4 text-emerald-600" /></Button>}
                        {!j.invoice_id && j.status !== "cancelled" && <Button size="sm" variant="ghost" onClick={() => generateInvoice(j)} data-testid={`job-invoice-${j.job_id}`} title="Generate invoice"><Receipt className="h-4 w-4 text-eagle-gold" /></Button>}
                        {j.invoice_id && <Button size="sm" variant="ghost" onClick={() => viewInvoice(j.invoice_id)} data-testid={`job-show-invoice-${j.job_id}`} title="Show invoice"><Eye className="h-4 w-4 text-blue-600" /></Button>}
                        {j.status !== "completed" && j.status !== "cancelled" && <Button size="sm" variant="ghost" onClick={() => setJobStatus(j.job_id, "cancelled")} title="Cancel"><XIcon className="h-4 w-4 text-orange-600" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={!!scheduling} onOpenChange={(o) => !o && setScheduling(null)}>
        <DialogContent className="max-w-lg">
          {scheduling && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">Schedule — {scheduling.customer_name}</DialogTitle></DialogHeader>
              <div className="text-sm text-muted-foreground mb-3">{scheduling.address}</div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Add an install date (Mon–Fri; Sat on demand; Sunday closed)</label>
                  <div className="flex gap-2 mt-1.5">
                    <Input type="date" value={dateInput} onChange={(e) => { setDateInput(e.target.value); checkDate(e.target.value); }} data-testid="schedule-date-input" />
                    <Button type="button" variant="outline" onClick={addDate} disabled={!dateInput || dayInfo?.closed}>Add</Button>
                  </div>
                  {dayInfo?.closed && <div className="text-xs text-red-600 mt-1.5">Closed Sundays — pick another day.</div>}
                  {dayInfo?.on_demand && !dayInfo?.closed && <div className="text-xs text-amber-600 mt-1.5">Saturday — on demand booking.</div>}
                  {dayInfo?.existing?.length > 0 && (
                    <div className="text-xs text-muted-foreground mt-1.5">Already booked that day: {dayInfo.existing.map(e => e.customer_name).join(", ")}</div>
                  )}
                </div>
                {scheduleDates.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Selected dates</div>
                    <div className="flex flex-wrap gap-2">
                      {scheduleDates.map(d => (
                        <span key={d} className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-full bg-eagle-gold/15 text-sm font-medium">
                          {d}
                          <button onClick={() => removeDate(d)} className="hover:text-red-600" aria-label={`Remove ${d}`}><XIcon className="h-3.5 w-3.5" /></button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setScheduling(null)}>Cancel</Button>
                <Button onClick={saveSchedule} disabled={busy} className="bg-eagle-gold text-eagle-ink hover:bg-eagle-goldHover" data-testid="schedule-save-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalIcon className="h-4 w-4 mr-2" />}Save schedule
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <InvoiceCreateDialog job={invoiceJob} open={!!invoiceJob} onClose={() => setInvoiceJob(null)} onCreated={() => load()} />
      <NewJobDialog open={newJobOpen} onClose={() => setNewJobOpen(false)} onCreated={() => load()} />
    </div>
  );
}